import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Space, Spin, Tabs, Typography } from 'antd';
import _ from 'lodash';
import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import { Link, match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import IRfiItem from '~Api/Application/IRfiItem';
import {
    applicationBorrowersListAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationGetAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import {
    applicationBorrowersSelector,
    applicationConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationPropertiesSelector,
    applicationRfiItemsSelector,
    applicationSelector,
} from '~Applications/selectors';
import ConditionDocumentsList from '~Applications/Application/ConditionDocumentsList';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import {
    approvedConditionDocumentExists,
} from '~Applications/utilities';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { aggregatorsSelector } from '~Aggregators/selectors';
import { aggregatorsListAction } from '~Aggregators/actions';
import IAggregator from '~Api/Aggregator/IAggregator';
import ConditionAddModal from './ConditionAddModal';
import ModalStateEnum from '~UI/ModalStateEnum';

const nextWorkflowStatuses: IDictionary<WorkflowStatusEnum> = {
    [WorkflowStatusEnum.Draft]: WorkflowStatusEnum.New,
    [WorkflowStatusEnum.New]: WorkflowStatusEnum.ConditionalApproval,
    [WorkflowStatusEnum.ConditionalApproval]: WorkflowStatusEnum.Underwriting,
    [WorkflowStatusEnum.Underwriting]: WorkflowStatusEnum.FormalApproval,
    [WorkflowStatusEnum.FormalApproval]: WorkflowStatusEnum.LegalDocuments,
    [WorkflowStatusEnum.LegalDocuments]: WorkflowStatusEnum.Settlement,
    [WorkflowStatusEnum.Settlement]: WorkflowStatusEnum.Warehoused,
};

interface IMatch {
    applicationUuid: string;
}

interface IState {
    isAddConditionModalOpen: ModalStateEnum;
    openModalEntityUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    aggregators: IDictionary<IAggregator>;
    application: IApplication;
    applicationEntityConditions: IDictionary<IDictionary<ICondition>>;
    applicationProperties: IDictionary<IApplicationProperty>;
    borrowers: IDictionary<IBorrower>;
    conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]>;
    rfiItems: IDictionary<IRfiItem>;
}

interface IPropsDispatch {
    aggregatorsList: () => void;
    applicationGet: () => void;
    borrowersList: () => void;
    conditionDocumentsList: () => void;
    conditionsList: () => void;
    rfiItemsList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Underwriting extends React.Component<Props> {
    private refreshInterval: NodeJS.Timeout;
    public state: IState = {
        isAddConditionModalOpen: ModalStateEnum.Off,
        openModalEntityUuid: null,
    };

    constructor(props: Props) {
        super(props);

        this.onCancelAddConditionModal = this.onCancelAddConditionModal.bind(this);
        this.onClickAddConditionModal = this.onClickAddConditionModal.bind(this);
    }

    public componentDidMount(): void {
        const {
            application,
            applicationEntityConditions,
            borrowers,
            conditionDocumentsByConditionUuid,
            rfiItems,
        } = this.props;

        if (!application) {
            this.props.applicationGet();
        }

        if (!borrowers) {
            this.props.borrowersList();
        }

        if (!conditionDocumentsByConditionUuid) {
            this.props.conditionDocumentsList();
        }

        if (!applicationEntityConditions) {
            this.props.conditionsList();
        }

        if (!rfiItems) {
            this.props.rfiItemsList();
        }

        this.refreshInterval = setInterval(() => {
            this.props.conditionDocumentsList();
            this.props.conditionsList();
            this.props.rfiItemsList();
        }, 5 * 60 * 1000);
    }

    public componentWillUnmount(): void {
        clearInterval(this.refreshInterval);
    }

    public render(): JSX.Element {
        const {
            aggregators,
            application,
            applicationEntityConditions,
            applicationProperties,
            borrowers,
            conditionDocumentsByConditionUuid,
            match,
            rfiItems,
        } = this.props;
        const { openModalEntityUuid, isAddConditionModalOpen } = this.state;

        if (
            !aggregators ||
            !application ||
            !applicationEntityConditions ||
            !applicationProperties ||
            !borrowers ||
            !conditionDocumentsByConditionUuid ||
            !rfiItems
        ) {
            return (
                <Layout applicationUuid={match.params.applicationUuid} section='underwriting'>
                    <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
                    <Spin/>
                </Layout>
            );
        }

        const borrowerTabsBlock: JSX.Element[] = _.map(borrowers, (borrower: IBorrower) => {
            const onClickAddApplicationBorrowerCondition: () => void = (): void => this.onClickAddConditionModal(borrower.uuid);
            const borrowerConditionModal: ReactElement = openModalEntityUuid === borrower.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
                <ConditionAddModal
                    application={application}
                    applicationBorrower={borrower}
                    isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                    onCancel={this.onCancelAddConditionModal}
                />
            );

            return (
                <Tabs.TabPane
                    tab={<Typography>{this.entityConditionDocumentsComplete(borrower.uuid) ? <CheckOutlined/> : <WarningOutlined/>} {borrower.dealBorrower.formattedName}</Typography>}
                    key={`borrower-${borrower.uuid}`}
                >
                    <ConditionDocumentsList
                        key={`condition-document-borrower-${borrower.uuid}`}
                        application={application}
                        applicationBorrower={borrower}
                    />
                    <Button className='add-condition' onClick={onClickAddApplicationBorrowerCondition}>Add Condition</Button>
                    {borrowerConditionModal}
                </Tabs.TabPane>
            );
        });

        const propertyTabsBlock: JSX.Element[] = _.map(applicationProperties, (applicationProperty: IApplicationProperty) => {
            const onClickAddApplicationPropertyCondition: () => void = (): void => this.onClickAddConditionModal(applicationProperty.uuid);
            const propertyConditionModal: ReactElement = openModalEntityUuid === applicationProperty.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
                <ConditionAddModal
                    application={application}
                    applicationProperty={applicationProperty}
                    isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                    onCancel={this.onCancelAddConditionModal}
                />
            );

            return (
                <Tabs.TabPane
                    tab={<Typography>{this.entityConditionDocumentsComplete(applicationProperty.uuid) ? <CheckOutlined/> : <WarningOutlined/>} {applicationProperty.dealProperty.streetAddress}</Typography>}
                    key={`property-${applicationProperty.uuid}`}
                >
                    <ConditionDocumentsList
                        key={`condition-document-property${applicationProperty.uuid}`}
                        application={application}
                        applicationProperty={applicationProperty}
                    />
                    <Button className='add-condition' onClick={onClickAddApplicationPropertyCondition}>Add Condition</Button>
                    {propertyConditionModal}
                </Tabs.TabPane>
            );
        });

        const solictorDownloadsButton: JSX.Element = !application.isExtension && (
            <Link to={`/applications/${application.uuid}/solicitor-downloads`}>
                <Button>Download Documents</Button>
            </Link>
        );

        const onClickAddApplicationCondition: () => void = (): void => this.onClickAddConditionModal(application.uuid);
        const applicationConditionModal: ReactElement = openModalEntityUuid === application.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
            <ConditionAddModal
                application={application}
                isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                onCancel={this.onCancelAddConditionModal}
            />
        );

        return (
            <Layout applicationUuid={match.params.applicationUuid} section='underwriting'>
                <Space className='actions'>
                    {solictorDownloadsButton}
                </Space>
                <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
                <Tabs defaultActiveKey={`application-${application.uuid}`} type='card'>
                    <Tabs.TabPane
                        tab={<Typography>{this.entityConditionDocumentsComplete(application.uuid) ? <CheckOutlined/> : <WarningOutlined/>} Application</Typography>}
                        key={`application-${application.uuid}`}
                    >
                        <ConditionDocumentsList
                            key={`condition-document-application-${application.uuid}`}
                            application={application}
                        />
                        <Button className='add-condition' onClick={onClickAddApplicationCondition}>Add Condition</Button>
                        {applicationConditionModal}
                    </Tabs.TabPane>
                    {borrowerTabsBlock}
                    {propertyTabsBlock}
                </Tabs>
            </Layout>
        );
    }

    private onClickAddConditionModal(openModalEntityUuid: string): void {
        this.setState({
            isAddConditionModalOpen: ModalStateEnum.Open,
            openModalEntityUuid,
        });
    }

    private onCancelAddConditionModal(): void {
        this.setState({
            isAddConditionModalOpen: ModalStateEnum.Closed,
            openModalEntityUuid: null,
        });
    }

    private entityConditionDocumentsComplete(entityUuid: string): boolean {
        const { application, applicationEntityConditions, conditionDocumentsByConditionUuid } = this.props;

        if (_.some(applicationEntityConditions[entityUuid] || [], (condition: ICondition) => (
            condition.requiredWorkflowStatuses.includes(nextWorkflowStatuses[application.workflowStatus]) &&
            !approvedConditionDocumentExists(conditionDocumentsByConditionUuid[condition.uuid] || [])
        ))) {
            return false;
        }

        return true;
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        aggregators: aggregatorsSelector(state),
        application: applicationSelector(state, ownProps.match.params.applicationUuid),
        applicationEntityConditions: applicationEntityConditionsSelector(state, ownProps.match.params.applicationUuid),
        applicationProperties: applicationPropertiesSelector(state, ownProps.match.params.applicationUuid),
        borrowers: applicationBorrowersSelector(state, ownProps.match.params.applicationUuid),
        conditionDocumentsByConditionUuid: applicationConditionDocumentsByConditionUuidSelector(state, ownProps.match.params.applicationUuid),
        rfiItems: applicationRfiItemsSelector(state, ownProps.match.params.applicationUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        aggregatorsList: () => dispatch(aggregatorsListAction()),
        applicationGet: () => dispatch(applicationGetAction(ownProps.match.params.applicationUuid)),
        borrowersList: () => dispatch(applicationBorrowersListAction(ownProps.match.params.applicationUuid)),
        conditionDocumentsList: () => dispatch(applicationConditionDocumentsListAction(ownProps.match.params.applicationUuid)),
        conditionsList: () => dispatch(applicationConditionsListAction(ownProps.match.params.applicationUuid)),
        rfiItemsList: () => dispatch(applicationRfiItemsListAction(ownProps.match.params.applicationUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Underwriting);
